// The claim form (finance-real, the user 2026-10-23: "make into a component the claim", and of its amount box: "this
// input should be the one that does the claim with the explainer and percentage"): one form wherever a claim is made or
// changed, the job drawer's and the invoice drawer's claims (ClaimsList), a team invoice's Claim another job and New team
// invoice's (ClaimAnother). The job picker shows when the context does not fix the job, Who claims (the one picker,
// DD-056) when it does not fix the person. The amount is ClaimAmountField: euros typed, the share of the job's price
// beside them ("40% of 250,00 €"), and under them the grey explainer in the job drawer's words, "Free to claim: X of its
// Y cap". What is free and how far a job is over its cap are the server's (TD-102): a new claim reads its job's
// `freeMinor`, an edited one its own `maxMinor` (the most it can be) and `overCapMinor`; the form compares the amount
// typed with them and works out no figure of its own, only the typed amount's share of the price. Over that, the refusal
// shows at once; the server's own (422, 409) shows in its place. An edit keeping or lowering the amount always saves, and
// only a raise past `maxMinor` is refused, as the server's claim_edit_refusal does: a saved claim already over the cap
// says by how much and still saves as it is (to someone else, say). Opened from a button, its first field takes focus. The footer is Cancel and "<verb> · <amount>" (DD-056): "Add the claim" for
// every new one, "Save" for a change, with Take off when it edits a saved claim (off its job, or off a team invoice).
// Escape closes the form alone (EscapeScope), never the drawer it sits in. The job form's claim
// (a team invoice's new job) is ClaimAmountField alone, inside that form, whose button makes the job and the claim.
// A child taking props and callbacks: the figures are the jobs' the drawers' hooks read (TD-102), so it has no hook.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { FormField } from "@/components/ui/FormField";
import { MoneyInput } from "@/components/ui/MoneyInput";
import { MoneyText } from "@/components/ui/MoneyText";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { formatMoney } from "@/utils/currency";

// `contract` names the user's job now (ENTITY_CONFIG).
const JOB = ENTITY_CONFIG.contract;
const jobWord = JOB.label.toLowerCase();

/** A job as a claim reads it: the server's price, cap and what is free on it for a new claim (TD-102). */
export interface ClaimJob {
  id: string;
  /** In the job picker: "Social Media Management · October 2026". */
  label: string;
  priceMinor: number;
  marginPct: number;
  capMinor: number;
  /** What is left to claim under the cap: LineJob's and ClaimableJob's `freeMinor`. */
  freeMinor: number;
}

/** A saved claim as the form edits it: JobClaim's (or TeamInvoiceClaim's) amount and the server's figures for it. */
export interface SavedClaim {
  amountMinor: number;
  /** The most it can be: the cap less the job's other claims. */
  maxMinor: number;
  /** How far its job's claims are over the cap, or 0. */
  overCapMinor: number;
}

/** The most this claim can be, as the server answers it: a saved claim's `maxMinor`, or the job's free room for a new one. */
export const freeFor = (job: ClaimJob, saved?: SavedClaim): number => saved?.maxMinor ?? job.freeMinor;

/** Why an amount is refused, or undefined. Keeping or lowering a saved amount always fits, even on a job over its cap:
 * only a raise past what it can be is refused. */
export function claimAmountError(amountMinor: number | null, job: ClaimJob, saved?: SavedClaim): string | undefined {
  if (amountMinor === null || amountMinor <= 0) return "An amount above zero, like 130,00";
  const free = freeFor(job, saved);
  if (amountMinor > free && amountMinor > (saved?.amountMinor ?? 0)) {
    return `Only ${formatMoney(free, "EUR")} is free to claim: its cap is ${formatMoney(job.capMinor, "EUR")}, the price less its ${job.marginPct}% margin.`;
  }
  return undefined;
}

/** Over what is free: the refusal shows as it is typed. An empty amount waits for the submit. */
const overFree = (amountMinor: number | null, job: ClaimJob | null, saved: SavedClaim | undefined): string | undefined =>
  job && amountMinor !== null && amountMinor > 0 ? claimAmountError(amountMinor, job, saved) : undefined;

/** Escape inside it closes it alone. It listens on window ahead of the drawer's Escape (Radix's, on the document), as the
 * DropdownMenu does, and leaves the key to a menu open (`data-dropdown-menu`), which closes first. */
export function EscapeScope({ onEscape, focusFirst = false, className, children }: { onEscape: () => void; focusFirst?: boolean; className?: string; children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const escape = useRef(onEscape);
  escape.current = onEscape;
  // Opened from a button, its first field takes focus, not the dialog around it.
  const first = useRef(focusFirst);
  useEffect(() => {
    if (first.current) root.current?.querySelector<HTMLElement>("button:not([disabled]), input:not([disabled])")?.focus();
  }, []);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || document.querySelector("[data-dropdown-menu]")) return;
      if (!(event.target instanceof Node) || !root.current?.contains(event.target)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      escape.current();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);
  return (
    <div ref={root} className={className}>
      {children}
    </div>
  );
}

interface FieldProps {
  ariaLabel: string;
  valueMinor: number | null;
  onChange: (minor: number | null) => void;
  /** The job claimed on; null while it is not known (a new job without its price and margin %). */
  job: ClaimJob | null;
  /** The saved claim, when it edits one. */
  saved?: SavedClaim;
  /** What the explainer says while the job is not known. */
  waiting?: string;
  /** Shown in the refusal's place: the server's, or a missing amount once submitted. */
  error?: string;
  label?: ReactNode;
  disabled?: boolean;
  autoFocus?: boolean;
}

/** The claim's amount: euros, the share of the job's price beside them, the cap and what is free under them. */
export function ClaimAmountField({ ariaLabel, valueMinor, onChange, job, saved, waiting, error, label, disabled = false, autoFocus = false }: FieldProps) {
  const refused = overFree(valueMinor, job, saved) ?? error;
  const free = job ? freeFor(job, saved) : 0;
  // A saved claim on a job over its cap (a prod split above it, or a cap lowered since) says by how much, the server's
  // figure, until the amount typed comes down to what is free.
  const overCap = saved !== undefined && saved.overCapMinor > 0 && (valueMinor ?? saved.amountMinor) > free;
  // The typed amount's share of the price: it formats the user's input, not a stored figure.
  const share = job && job.priceMinor > 0 && valueMinor !== null && valueMinor > 0 ? Math.round((valueMinor / job.priceMinor) * 100) : null;
  const field = (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <span className="block w-36 shrink-0">
          <MoneyInput ariaLabel={ariaLabel} currency="EUR" valueMinor={valueMinor} onChange={onChange} invalid={refused !== undefined} disabled={disabled} autoFocus={autoFocus} />
        </span>
        {job && (
          <span className="min-w-0 text-xs text-gray-500">
            {share !== null ? `${share}% of ` : "of "}
            <MoneyText amountMinor={job.priceMinor} />
          </span>
        )}
      </div>
      <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
        {job ? (
          <span>
            {"Free to claim: "}
            <MoneyText amountMinor={free} strong />
            {" of its "}
            <MoneyText amountMinor={job.capMinor} />
            {" cap."}
            {overCap && saved && (
              <span className="text-amber-700">
                {" Over the cap by "}
                <MoneyText amountMinor={saved.overCapMinor} strong />
                {": lower it to "}
                <MoneyText amountMinor={free} strong />
                {" or less."}
              </span>
            )}
          </span>
        ) : (
          (waiting ?? `What is free to claim shows once the ${jobWord} is picked.`)
        )}
      </div>
      {refused && <p className="text-xs text-red-600">{refused}</p>}
    </div>
  );
  return label ? <FormField label={label}>{field}</FormField> : field;
}

export interface ClaimDraft {
  jobId: string;
  /** Null where the context fixes the person. */
  personId: string | null;
  amountMinor: number;
}

interface FormProps {
  /** The job, when the context fixes it; otherwise `jobs` to pick from. */
  job?: ClaimJob;
  jobs?: ClaimJob[];
  /** Who claims, picked; omitted where the context fixes the person. */
  people?: { id: string; name: string }[];
  /** A saved claim being changed: its person, its amount and the server's figures for it, and Take off. */
  saved?: SavedClaim & { personId: string };
  /** At the form's top: whose claim on which job ("Cassie · Comms · October 2026"), where the list names several jobs. */
  heading?: string;
  /** Under the heading: the job's client, where the list holds several clients' jobs (a team invoice). Never before the job's
   * name (jobLabel). */
  subheading?: string;
  /** Beside the heading: a way to the job ("Open the job"), where the list is not the job's. */
  headingAside?: ReactNode;
  /** The amount filled in at first: the job's free amount on a team invoice's Claim another job. */
  initialAmountMinor?: number;
  /** The submit's verb (DD-056): "Add the claim" for a new claim, "Save" for a change. */
  verb: string;
  /** Omitted where the job takes no claim now: the form offers only Take off. */
  canSubmit?: boolean;
  /** A line under the field: why it can only be removed. */
  note?: string;
  pending: boolean;
  removing?: boolean;
  /** The server's refusal of the last submit or Take off. */
  refused?: string;
  onSubmit: (draft: ClaimDraft) => void;
  onRemove?: () => void;
  onCancel: () => void;
}

export function ClaimForm({ job: fixed, jobs = [], people, saved, heading, subheading, headingAside, initialAmountMinor, verb, canSubmit = true, note, pending, removing = false, refused, onSubmit, onRemove, onCancel }: FormProps) {
  const [jobId, setJobId] = useState(fixed?.id ?? (jobs.length === 1 ? jobs[0].id : ""));
  const [personId, setPersonId] = useState(saved?.personId ?? "");
  const [amountMinor, setAmountMinor] = useState<number | null>(saved?.amountMinor ?? initialAmountMinor ?? null);
  const [touched, setTouched] = useState(false);
  const job = fixed ?? jobs.find((j) => j.id === jobId) ?? null;
  const busy = pending || removing;
  const missing = !job ? `Pick the ${jobWord} it claims on` : people && personId === "" ? "Pick who claims" : undefined;
  const amountError = job ? claimAmountError(amountMinor, job, saved) : undefined;
  const submit = () => {
    setTouched(true);
    if (missing || amountError || !job || amountMinor === null) return;
    onSubmit({ jobId: job.id, personId: people ? personId : null, amountMinor });
  };
  return (
    <EscapeScope onEscape={() => !busy && onCancel()} focusFirst={saved === undefined} className="space-y-3 rounded-lg bg-gray-50 p-3">
      {(heading || headingAside) && (
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          {heading && (
            <p className="min-w-0 text-sm font-medium text-gray-900 [overflow-wrap:anywhere]">
              {heading}
              {subheading && <span className="block text-xs font-normal text-gray-500">{subheading}</span>}
            </p>
          )}
          {headingAside}
        </div>
      )}
      {!fixed && (
        <FormField label={JOB.label}>
          <OptionDropdown variant="input" ariaLabel={`The ${jobWord} it claims on`} value={jobId === "" ? null : jobId} placeholder={`Which ${jobWord}`} disabled={busy} options={jobs.map((j) => ({ value: j.id, label: j.label }))} onChange={setJobId} />
        </FormField>
      )}
      {people && (
        <FormField label="Who claims">
          <OptionDropdown variant="input" ariaLabel="Who claims" value={personId === "" ? null : personId} placeholder="Who claims" disabled={busy} options={people.map((p) => ({ value: p.id, label: p.name }))} onChange={setPersonId} />
        </FormField>
      )}
      <ClaimAmountField
        label="Amount"
        ariaLabel="Amount claimed"
        valueMinor={amountMinor}
        onChange={setAmountMinor}
        job={job}
        saved={saved}
        error={refused ?? (touched ? (missing ?? amountError) : undefined)}
        disabled={busy}
        autoFocus={saved !== undefined}
      />
      {note && <p className="text-xs text-gray-500">{note}</p>}
      <div className="flex items-center gap-2">
        {onRemove && (
          <Button size="sm" variant="ghost" disabled={busy} isLoading={removing} onClick={onRemove}>
            Take off
          </Button>
        )}
        <span className="flex-1" />
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        {canSubmit && (
          <Button size="sm" disabled={busy} isLoading={pending} onClick={submit}>
            {amountMinor !== null && amountMinor > 0 ? `${verb} · ${formatMoney(amountMinor, "EUR")}` : verb}
          </Button>
        )}
      </div>
    </EscapeScope>
  );
}
